// The followed route and the activities on it — port of the route, navigation and recording parts of
// TrailBivvy/App/AppEnvironment.swift (setActiveRoute, restoreActiveRoute, start/stopNavigation, start/pause/resume/
// stopRecording, learnWalkingPerformance, LA-9 navigation marker). Custom routes (combined, split, imported stages
// not in the store) are kept in IndexedDB so the same route comes back after a reload.

import { computed, effect, signal } from '@preact/signals';
import type { EpochMs, TrailStage, UserTrip } from '../core/models';
import { trailRouteFromStages } from '../data/route';
import { encodeTrailDataset, decodeTrailDataset } from '../data/decode';
import { HikingTimeEstimator, OffRouteConfiguration, WalkingPerformanceModel } from '../nav';
import { kvStore, type KeyValueStore } from '../storage/idb';
import type { Prefs } from '../storage/prefs';
import type { DataController } from './dataController';
import { NavigationSession, type OffRouteAlerter } from './navigationSession';
import { TripRepository } from './repositories';
import { TripRecorder } from './tripRecorder';

const customRouteKey = 'customRoute';

export class RouteController {
  readonly walkingModel = signal<WalkingPerformanceModel>(new WalkingPerformanceModel());
  readonly navigation: NavigationSession;
  readonly recorder: TripRecorder;
  readonly trips: TripRepository;
  /** A start on a synthetic or approximate line waits for the hiker's confirmation (iOS RootView alert). */
  readonly navigationNeedsConfirmation = signal(false);
  readonly routeIsNotForNavigation = computed(() =>
    this.navigation.stages.value.some((s) => s.dataQuality === 'demoSynthetic' || s.dataQuality === 'approximate' || s.provenance.isSynthetic),
  );
  readonly liveActivity = computed(
    () => this.navigation.isActive.value || (this.recorder.isRecording.value && !this.recorder.isPaused.value),
  );

  private readonly customStore: KeyValueStore<unknown>;

  constructor(
    private readonly data: DataController,
    private readonly prefs: Prefs,
    private readonly now: () => EpochMs,
    alerter?: OffRouteAlerter,
    options: { customStore?: KeyValueStore<unknown>; trips?: TripRepository } = {},
  ) {
    this.customStore = options.customStore ?? kvStore('packs');
    this.walkingModel.value = prefs.walkingModel.value ? WalkingPerformanceModel.fromJSON(prefs.walkingModel.value) : new WalkingPerformanceModel();
    const offRoute = prefs.offRouteConfiguration.value
      ? OffRouteConfiguration.make(prefs.offRouteConfiguration.value as Partial<OffRouteConfiguration>)
      : OffRouteConfiguration.make();
    this.navigation = new NavigationSession(now, offRoute, this.walkingModel.value.estimator, alerter);
    this.trips = options.trips ?? new TripRepository();
    this.recorder = new TripRecorder(this.trips, now);
    this.recorder.onFinished = (trip) => this.learnWalkingPerformance(trip);

    // Re-target the route whenever the data changes (iOS: end of reloadData).
    effect(() => {
      this.data.generation.value;
      void this.reconcileRoute();
    });
  }

  async start(): Promise<void> {
    await this.recorder.restoreActiveRecording();
  }

  get estimator(): HikingTimeEstimator {
    return this.walkingModel.value.estimator;
  }

  /** Follows the given stages (in order), optionally reversed. */
  setActiveRoute(stageIDs: string[], reversed = false): void {
    const store = this.data.dataStore.value;
    const stages = stageIDs.map((id) => store.stage(id)).filter((s): s is TrailStage => !!s);
    if (stages.length === 0) return;
    const route = trailRouteFromStages(stages, { reversed });
    this.navigation.setRoute(route, reversed ? [...stages].reverse() : stages);
    void this.customStore.delete(customRouteKey);
    this.prefs.activeStageIDs.value = stageIDs;
    this.prefs.activeRouteReversed.value = reversed;
  }

  /** Follows custom stages (combined/split) that are not in the store; kept for the next launch. */
  async setActiveCustomRoute(stages: TrailStage[]): Promise<void> {
    if (stages.length === 0) return;
    this.navigation.setRoute(trailRouteFromStages(stages), stages);
    this.prefs.activeStageIDs.value = [];
    await this.customStore.set(
      customRouteKey,
      encodeTrailDataset({
        metadata: { id: 'custom-route', name: 'Custom route', version: '1', generatedAt: this.now(), isSynthetic: false, provenance: [] },
        trails: [],
        stages,
        pointsOfInterest: [],
        protectedAreas: [],
        regulations: [],
        regulatoryCoverage: [],
      }),
    );
  }

  /** Back to the whole followed trail. */
  followWholeTrail(): void {
    this.prefs.activeStageIDs.value = [];
    this.prefs.activeRouteReversed.value = false;
    void this.customStore.delete(customRouteKey);
    void this.restoreActiveRoute();
  }

  /** Called after each data reload: restores the route when it no longer matches what the hiker follows. */
  private async reconcileRoute(): Promise<void> {
    const route = this.navigation.route.value;
    const storedIDs = this.prefs.activeStageIDs.value;
    const activeTrailID = this.data.activeTrailID.value;
    const trailDiffers = route?.trailID !== activeTrailID && !!activeTrailID && !!this.data.dataStore.value.trail(activeTrailID);
    const custom = await this.customStore.get(customRouteKey).catch(() => undefined);
    const idsDiffer =
      storedIDs.length > 0 &&
      route !== undefined &&
      route.stageIDs.join() !== storedIDs.join() &&
      route.stageIDs.join() !== [...storedIDs].reverse().join();
    if (!route || idsDiffer || (storedIDs.length === 0 && !custom && trailDiffers)) {
      await this.restoreActiveRoute();
    }
    if (!activeTrailID && this.data.importedTrails.value.length === 0 && storedIDs.length === 0 && !custom) {
      this.navigation.clearRoute();
    }
  }

  private async restoreActiveRoute(): Promise<void> {
    const store = this.data.dataStore.value;
    const ids = this.prefs.activeStageIDs.value;
    const reversed = this.prefs.activeRouteReversed.value;
    const custom = ids.length === 0 ? await this.customStore.get(customRouteKey).catch(() => undefined) : undefined;
    if (custom) {
      try {
        const stages = decodeTrailDataset(custom).stages;
        if (stages.length > 0) {
          this.navigation.setRoute(trailRouteFromStages(stages), stages);
          return;
        }
      } catch {
        // unreadable custom route: fall through to the followed trail
      }
    }
    if (ids.length > 0 && ids.every((id) => store.stage(id))) {
      this.setActiveRoute(ids, reversed);
      return;
    }
    const trail = this.data.primaryTrail.value ?? this.data.importedTrails.value[0];
    if (trail) {
      const stages = store.stagesForTrail(trail.id);
      if (stages.length > 0) this.navigation.setRoute(trailRouteFromStages(stages, { name: trail.name }), stages);
    }
  }

  // MARK: Navigation (iOS LA-9: the marker makes the next launch open on the Map)

  /** Starts navigation; false when the hiker must first confirm a line that is not for navigation. */
  startNavigation(confirmed = false): boolean {
    if (!confirmed && this.routeIsNotForNavigation.value) {
      this.navigationNeedsConfirmation.value = true;
      return false;
    }
    this.navigationNeedsConfirmation.value = false;
    this.navigation.start();
    this.prefs.navigationRunningAt.value = this.now();
    return true;
  }

  stopNavigation(): void {
    this.navigation.stop();
    this.prefs.navigationRunningAt.value = undefined;
  }

  /** Refreshes the marker when the page is hidden (iOS sceneDidEnterBackground). */
  markNavigationIfRunning(): void {
    if (this.navigation.isActive.value) this.prefs.navigationRunningAt.value = this.now();
  }

  // MARK: Recording

  startRecording(defaultName: string): void {
    const route = this.navigation.route.value;
    this.recorder.start(route?.name || defaultName, route?.trailID);
  }

  /** Never learns from trips with transport in them (implausible hiking speed), like iOS. */
  private learnWalkingPerformance(trip: UserTrip): void {
    const s = trip.statistics;
    if (!(s.movingTime > 0) || s.averageMovingSpeed > 2.5) return;
    const model = WalkingPerformanceModel.fromJSON(this.walkingModel.value.toJSON());
    model.record(s.movingTime, s.distance, s.ascent, s.descent);
    this.walkingModel.value = model;
    this.prefs.walkingModel.value = model.toJSON();
    this.navigation.estimator = model.estimator;
  }
}
